import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  MenuItemOption,
  useMediaQuery,
  Text,
  HStack
} from '@chakra-ui/react'

import { useTranslation } from 'react-i18next'
import SelectCheckboxMenu from 'components/SelectCheckboxMenu'
import {
  useTypeOfPropertiesQuery,
  useRentalTermsQuery,
  usePropertyAvailabilitiesQuery,
  Enum_Propertycategory_Type
} from 'generated/graphql'

import { SearchInitialValues } from 'types'
import React from 'react'
import { VscDash, VscAdd, VscClearAll } from 'react-icons/vsc'
import { amountFormatter } from 'utils'
import { useGeolocation } from 'hooks'

type FilterProps = Omit<SearchInitialValues, 'search' | 'selectedArea'> & {
  rooms?: number
  prices?: number[]
  isResult?: boolean
  removeFilters: () => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
}

const Filters: React.FC<FilterProps> = ({
  prices,
  rooms,
  removeFilters,
  propertyTypes,
  setFieldValue,
  maxPrice,
  minPrice,
  bedRooms,
  bathRooms,
  rentalTerms,
  propertyAvailabilities,
  furnished,
  tab,
  isResult
}) => {
  const { currency } = useGeolocation()
  const { t, i18n } = useTranslation()
  const translator = i18n as any
  const lang = translator.language as string
  const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
  const { data: typeOfProperties, loading: typeOfPropertiesLoading } = useTypeOfPropertiesQuery({
    variables: { start: 0, limit: 20 },
    fetchPolicy: 'cache-first'
  })
  const { data: rentalTermsData, loading: rentalTermsLoading } = useRentalTermsQuery({
    variables: { start: 0, limit: 20 },
    fetchPolicy: 'cache-first'
  })
  const { data: propertyAvailabilitiesData, loading: propertyAvailabilitiesLoading } =
    usePropertyAvailabilitiesQuery({
      variables: { start: 0, limit: 20 },
      fetchPolicy: 'cache-first'
    })

  const filterSection = React.useRef<HTMLDivElement>(null)

  return isResult && !isTabletOrMobile ? (
    <Flex
      flexWrap="wrap"
      display={isTabletOrMobile ? 'grid' : 'flex'}
      gridTemplateColumns="repeat(2, 1fr)"
      gridColumnGap={isResult ? 6 : isTabletOrMobile ? 3 : 12}
      align="flex-end"
      ref={filterSection}
    >
      <HStack spacing={3} w="100%" justify="space-between">
        <SelectCheckboxMenu
          label={t('min_price')}
          title={t('price')}
          closeOnSelect={true}
          flex={1}
          defaultValue={minPrice}
          type="radio"
          buttonTitle={minPrice ? `${amountFormatter(parseInt(minPrice), currency)}` : undefined}
          onChange={(values) => setFieldValue('minPrice', values)}
        >
          {maxPrice
            ? prices
                ?.slice(
                  0,
                  prices?.findIndex((price) => price === parseInt(maxPrice)) || prices?.length
                )
                .map((price) => (
                  <MenuItemOption key={price} value={price.toString()}>
                    {amountFormatter(price, currency)}
                  </MenuItemOption>
                ))
            : prices?.map((price) => (
                <MenuItemOption key={price} value={price.toString()}>
                  {amountFormatter(price, currency)}
                </MenuItemOption>
              ))}
        </SelectCheckboxMenu>

        <SelectCheckboxMenu
          title={t('price')}
          type="radio"
          flex={1}
          closeOnSelect={true}
          defaultValue={maxPrice}
          buttonTitle={maxPrice ? `${amountFormatter(parseInt(maxPrice), currency)}` : undefined}
          label={t('max_price')}
          onChange={(values) => setFieldValue('maxPrice', values)}
        >
          {minPrice
            ? prices
                ?.slice(
                  prices?.findIndex((price) => price === parseInt(minPrice)) || 0,
                  prices?.length
                )
                .map((price) => (
                  <MenuItemOption key={price} value={price.toString()}>
                    {amountFormatter(price, currency)}
                  </MenuItemOption>
                ))
            : prices?.map((price) => (
                <MenuItemOption key={price} value={price.toString()}>
                  {amountFormatter(price, currency)}
                </MenuItemOption>
              ))}
        </SelectCheckboxMenu>
      </HStack>
      <SelectCheckboxMenu
        label={t('type_of_property')}
        title={t('type_of_property')}
        w="100%"
        mr={isTabletOrMobile ? 0 : undefined}
        gridColumn={isTabletOrMobile ? 'span 2' : undefined}
        isLoaded={!typeOfPropertiesLoading}
        defaultValue={propertyTypes}
        buttonTitle={
          propertyTypes.length > 1 ? `${propertyTypes.length} sélectionné` : propertyTypes[0]
        }
        onChange={(values) => setFieldValue('propertyTypes', values)}
      >
        {typeOfProperties?.typeOfProperties?.map((type: any) => (
          <MenuItemOption value={type?.[`name_${lang}`]} key={type?.id}>
            {type?.[`name_${lang}`]}
          </MenuItemOption>
        ))}
      </SelectCheckboxMenu>
      <SelectCheckboxMenu
        title={t('rooms')}
        label={t('rooms')}
        defaultValue={bedRooms}
        type="radio"
        closeOnSelect={true}
        buttonTitle={bedRooms ? `${bedRooms} +` : undefined}
        onChange={(values) => setFieldValue('bedRooms', values)}
      >
        {Array.from(Array(rooms).keys())
          .slice(1)
          .map((num) => (
            <MenuItemOption key={num} value={num.toString()}>
              {num} +
            </MenuItemOption>
          ))}
      </SelectCheckboxMenu>
      <SelectCheckboxMenu
        title={t('property_availability')}
        label={t('property_availability')}
        isLoaded={!propertyAvailabilitiesLoading}
        type="radio"
        defaultValue={propertyAvailabilities}
        closeOnSelect={true}
        buttonTitle={propertyAvailabilities ? propertyAvailabilities : undefined}
        onChange={(values) => setFieldValue('propertyAvailabilities', values)}
      >
        {propertyAvailabilitiesData?.propertyAvailabilities?.map((availability: any) => (
          <MenuItemOption value={availability?.[`name_${lang}`] as string} key={availability?.id}>
            {availability?.[`name_${lang}`]}
          </MenuItemOption>
        ))}
      </SelectCheckboxMenu>
      <Accordion
        allowMultiple
        width="100%"
        gridColumn={isTabletOrMobile ? 'span 2' : undefined}
        my={6}
      >
        <AccordionItem border="none">
          {({ isExpanded }) => (
            <>
              <AccordionButton as={Button} width={isTabletOrMobile ? '100%' : 'auto'}>
                <Text fontWeight="semibold" fontSize="xs" mr={2}>
                  {t('more_filters')}
                </Text>
                {isExpanded ? <VscDash /> : <VscAdd />}
              </AccordionButton>

              <AccordionPanel p={0}>
                <Flex
                  flexWrap="wrap"
                  display={isTabletOrMobile ? 'grid' : 'flex'}
                  gridTemplateColumns="repeat(2, 1fr)"
                  gridColumnGap={isResult ? 6 : isTabletOrMobile ? 3 : 12}
                  align="flex-end"
                >
                  {tab?.type === Enum_Propertycategory_Type.Rent && (
                    <SelectCheckboxMenu
                      label={t('rental_terms')}
                      title={t('rental_terms')}
                      isLoaded={!rentalTermsLoading}
                      defaultValue={rentalTerms}
                      buttonTitle={
                        rentalTerms.length > 1
                          ? `${rentalTerms.length} sélectionné`
                          : rentalTerms[0]
                      }
                      onChange={(values) => setFieldValue('rentalTerms', values)}
                    >
                      {rentalTermsData?.rentalTerms?.map((term: any) => (
                        <MenuItemOption value={term?.[`name_${lang}`]} key={term?.id}>
                          {term?.[`name_${lang}`]}
                        </MenuItemOption>
                      ))}
                    </SelectCheckboxMenu>
                  )}
                  <SelectCheckboxMenu
                    title={t('furnished')}
                    label={t('furnished')}
                    type="radio"
                    defaultValue={furnished ? 'Yes' : 'No'}
                    closeOnSelect={true}
                    buttonTitle={furnished === null ? undefined : furnished ? t('yes') : t('no')}
                    onChange={(values) => setFieldValue('furnished', values === 'yes')}
                  >
                    <MenuItemOption value="yes">{t('yes')}</MenuItemOption>
                    <MenuItemOption value="no">{t('no')}</MenuItemOption>
                  </SelectCheckboxMenu>
                  <SelectCheckboxMenu
                    title={t('bath_rooms')}
                    label={t('bath_rooms')}
                    type="radio"
                    defaultValue={bathRooms}
                    closeOnSelect={true}
                    buttonTitle={bathRooms ? `${bathRooms} +` : undefined}
                    onChange={(values) => setFieldValue('bathRooms', values)}
                  >
                    {Array.from(Array(rooms).keys())
                      .slice(1)
                      .map((num) => (
                        <MenuItemOption key={num} value={num.toString()}>
                          {num} +
                        </MenuItemOption>
                      ))}
                  </SelectCheckboxMenu>
                </Flex>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
      <Flex align="center" onClick={removeFilters} cursor="pointer">
        <VscClearAll />{' '}
        <Text fontSize="xs" fontWeight="semibold" minW="fit-content" mx={2}>
          {t('reset_filters')}
        </Text>
      </Flex>
    </Flex>
  ) : (
    <Flex
      flexWrap="wrap"
      display={isTabletOrMobile ? 'grid' : 'flex'}
      gridTemplateColumns="repeat(2, 1fr)"
      gridColumnGap={isResult ? 6 : isTabletOrMobile ? 3 : 12}
      gridRowGap={0}
      align="flex-end"
      ref={filterSection}
    >
      <SelectCheckboxMenu
        label={t('type_of_property')}
        title={t('type_of_property')}
        mr={isTabletOrMobile ? 0 : undefined}
        gridColumn={isTabletOrMobile ? 'span 2' : undefined}
        isLoaded={!typeOfPropertiesLoading}
        defaultValue={propertyTypes}
        buttonTitle={
          propertyTypes.length > 1 ? `${propertyTypes.length} sélectionné` : propertyTypes[0]
        }
        onChange={(values) => setFieldValue('propertyTypes', values)}
      >
        {typeOfProperties?.typeOfProperties?.map((type: any) => (
          <MenuItemOption value={type?.[`name_${lang}`]} key={type?.id}>
            {type?.[`name_${lang}`]}
          </MenuItemOption>
        ))}
      </SelectCheckboxMenu>

      <SelectCheckboxMenu
        label={t('min_price')}
        title={t('price')}
        closeOnSelect={true}
        defaultValue={minPrice}
        type="radio"
        buttonTitle={minPrice ? `${amountFormatter(parseInt(minPrice), currency)}` : undefined}
        onChange={(values) => setFieldValue('minPrice', values)}
      >
        {maxPrice
          ? prices
              ?.slice(
                0,
                prices?.findIndex((price) => price === parseInt(maxPrice)) || prices?.length
              )
              .map((price) => (
                <MenuItemOption key={price} value={price.toString()}>
                  {amountFormatter(price, currency)}
                </MenuItemOption>
              ))
          : prices?.map((price) => (
              <MenuItemOption key={price} value={price.toString()}>
                {amountFormatter(price, currency)}
              </MenuItemOption>
            ))}
      </SelectCheckboxMenu>

      <SelectCheckboxMenu
        title={t('price')}
        type="radio"
        closeOnSelect={true}
        defaultValue={maxPrice}
        buttonTitle={maxPrice ? `${amountFormatter(parseInt(maxPrice), currency)}` : undefined}
        label={t('max_price')}
        onChange={(values) => setFieldValue('maxPrice', values)}
      >
        {minPrice
          ? prices
              ?.slice(
                prices?.findIndex((price) => price === parseInt(minPrice)) || 0,
                prices?.length
              )
              .map((price) => (
                <MenuItemOption key={price} value={price.toString()}>
                  {amountFormatter(price, currency)}
                </MenuItemOption>
              ))
          : prices?.map((price) => (
              <MenuItemOption key={price} value={price.toString()}>
                {amountFormatter(price, currency)}
              </MenuItemOption>
            ))}
      </SelectCheckboxMenu>
      <SelectCheckboxMenu
        title={t('rooms')}
        label={t('rooms')}
        defaultValue={bedRooms}
        type="radio"
        closeOnSelect={true}
        buttonTitle={bedRooms ? `${bedRooms} +` : undefined}
        onChange={(values) => setFieldValue('bedRooms', values)}
      >
        {Array.from(Array(rooms).keys())
          .slice(1)
          .map((num) => (
            <MenuItemOption key={num} value={num.toString()}>
              {num} +
            </MenuItemOption>
          ))}
      </SelectCheckboxMenu>
      <SelectCheckboxMenu
        title={t('property_availability')}
        label={t('property_availability')}
        isLoaded={!propertyAvailabilitiesLoading}
        type="radio"
        defaultValue={propertyAvailabilities}
        closeOnSelect={true}
        buttonTitle={propertyAvailabilities ? propertyAvailabilities : undefined}
        onChange={(values) => setFieldValue('propertyAvailabilities', values)}
      >
        {propertyAvailabilitiesData?.propertyAvailabilities?.map((availability: any) => (
          <MenuItemOption value={availability?.[`name_${lang}`] as string} key={availability?.id}>
            {availability?.[`name_${lang}`]}
          </MenuItemOption>
        ))}
      </SelectCheckboxMenu>

      <Accordion
        allowMultiple
        width="100%"
        gridColumn={isTabletOrMobile ? 'span 2' : undefined}
        my={6}
      >
        <AccordionItem border="none">
          {({ isExpanded }) => (
            <>
              <AccordionButton as={Button} width={isTabletOrMobile ? '100%' : 'auto'}>
                <Text fontWeight="semibold" fontSize="xs" mr={2}>
                  {t('more_filters')}
                </Text>
                {isExpanded ? <VscDash /> : <VscAdd />}
              </AccordionButton>

              <AccordionPanel p={0}>
                <Flex
                  flexWrap="wrap"
                  display={isTabletOrMobile ? 'grid' : 'flex'}
                  gridTemplateColumns="repeat(2, 1fr)"
                  gridColumnGap={isResult ? 6 : isTabletOrMobile ? 3 : 12}
                  align="flex-end"
                >
                  {tab?.type === Enum_Propertycategory_Type.Rent && (
                    <SelectCheckboxMenu
                      label={t('rental_terms')}
                      title={t('rental_terms')}
                      isLoaded={!rentalTermsLoading}
                      defaultValue={rentalTerms}
                      buttonTitle={
                        rentalTerms.length > 1
                          ? `${rentalTerms.length} sélectionné`
                          : rentalTerms[0]
                      }
                      onChange={(values) => setFieldValue('rentalTerms', values)}
                    >
                      {rentalTermsData?.rentalTerms?.map((term: any) => (
                        <MenuItemOption value={term?.[`name_${lang}`]} key={term?.id}>
                          {term?.[`name_${lang}`]}
                        </MenuItemOption>
                      ))}
                    </SelectCheckboxMenu>
                  )}
                  <SelectCheckboxMenu
                    title={t('furnished')}
                    label={t('furnished')}
                    type="radio"
                    defaultValue={furnished ? 'Yes' : 'No'}
                    closeOnSelect={true}
                    buttonTitle={furnished === null ? undefined : furnished ? t('yes') : t('no')}
                    onChange={(values) => setFieldValue('furnished', values === 'yes')}
                  >
                    <MenuItemOption value="yes">{t('yes')}</MenuItemOption>
                    <MenuItemOption value="no">{t('no')}</MenuItemOption>
                  </SelectCheckboxMenu>
                  <SelectCheckboxMenu
                    title={t('bath_rooms')}
                    label={t('bath_rooms')}
                    type="radio"
                    defaultValue={bathRooms}
                    closeOnSelect={true}
                    buttonTitle={bathRooms ? `${bathRooms} +` : undefined}
                    onChange={(values) => setFieldValue('bathRooms', values)}
                  >
                    {Array.from(Array(rooms).keys())
                      .slice(1)
                      .map((num) => (
                        <MenuItemOption key={num} value={num.toString()}>
                          {num} +
                        </MenuItemOption>
                      ))}
                  </SelectCheckboxMenu>
                </Flex>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
      <Flex align="center" onClick={removeFilters} cursor="pointer">
        <VscClearAll />{' '}
        <Text fontSize="xs" fontWeight="semibold" minW="fit-content" mx={2}>
          {t('reset_filters')}
        </Text>
      </Flex>
    </Flex>
  )
}

export default Filters
